import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Blocks, BookOpen, Briefcase, Brain, Car, ChevronDown, Clapperboard, Code2, Gamepad2, Globe, Globe2,
  GraduationCap, HeartPulse, Headphones, Landmark, Languages, Lightbulb, MailQuestion, Library, Menu,
  MonitorCog, Moon, Scale, Search, ShoppingCart, Smartphone, Sparkles, Sun, TrendingUp, Trophy, User, UserSearch, X,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../api";
import { APP_CATEGORIES } from "../data/apps";
import { SiteSearch } from "./SiteSearch";

const BOOK_ICONS = { BookOpen, Search, Sparkles, TrendingUp, Brain, Lightbulb, Languages, Blocks, GraduationCap };
const APP_CAT_ICONS = { edukacja: GraduationCap, motoryzacja: Car, zdrowie: HeartPulse, firma: Briefcase, prawo: Scale, technologia: MonitorCog, multimedia: Clapperboard, sport: Trophy, kariera: UserSearch };
const GAME_SUBJECTS = [
  { slug: "matematyka", label: "Matematyka", icon: Sparkles },
  { slug: "angielski", label: "Angielski", icon: Languages },
  { slug: "geografia", label: "Geografia", icon: Globe2 },
  { slug: "przyroda", label: "Przyroda", icon: Blocks },
  { slug: "historia", label: "Historia", icon: Landmark },
  { slug: "polski", label: "Polski", icon: BookOpen },
  { slug: "kodowanie", label: "Kodowanie", icon: Code2 },
];
const BOOK_CATS_EXCLUDE = new Set(["nauka-jezykow", "edukacja-dzieci", "korepetytor-ai", "pakiety"]); // to sa kategorie aplikacji, nie ksiazek — patrz Home.jsx bookCategoriesFor

function LanguageSwitcher() {
  const { lang, setLang, languages, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find((l) => l.code === lang) || languages[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="lang-switcher-button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full glass-panel px-3 py-2.5 text-slate-200 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
        aria-label={t("nav.langSwitcher.aria")}
      >
        <Globe className="h-5 w-5" />
        <span className="hidden text-xs font-bold uppercase sm:inline">{current.code}</span>
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 overflow-hidden rounded-2xl border border-cyan-500/15 bg-slate-950/95 py-1.5 shadow-xl shadow-black/40 backdrop-blur-2xl">
          {languages.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors hover:bg-cyan-500/10 ${l.code === lang ? "text-cyan-300 font-semibold" : "text-slate-300"}`}
            >
              <span>{l.flag}</span> {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Mega-menu: pokazuje sie na hover (desktop) nad etykieta w pasku nawigacji, z lista podkategorii
// do bezposredniego skoku — nie trzeba juz wracac na strone glowna, zeby filtrowac po gatunku/kategorii.
function NavMenu({ label, labelClassName, testid, isOpen, onOpen, onCloseSoon, onLabelClick, items, columns }) {
  return (
    <div className="relative shrink-0" onMouseEnter={onOpen} onMouseLeave={onCloseSoon}>
      <button
        data-testid={testid}
        onClick={onLabelClick}
        className={`flex items-center gap-1 text-sm transition-colors ${labelClassName}`}
      >
        {label}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className={`glass-panel absolute left-1/2 top-full z-50 mt-3 ${columns ? "w-[30rem]" : "w-72"} -translate-x-1/2 overflow-hidden rounded-2xl p-2 shadow-xl shadow-black/40`}
            data-testid={`${testid}-panel`}
          >
            {columns ? (
              <div className="grid grid-cols-2 gap-2">
                {columns.map((col) => (
                  <div key={col.key} className="grid grid-cols-1 content-start gap-0.5">
                    <button onClick={col.onClick} data-testid={`${testid}-col-${col.key}`} className="rounded-xl px-3 pb-1 pt-1.5 text-left text-[11px] font-bold uppercase tracking-widest text-amber-300 hover:text-amber-200">{col.title}</button>
                    {col.items.map((it) => {
                      const Icon = it.icon;
                      return (
                        <button key={it.key} data-testid={`${testid}-item-${col.key}-${it.key}`} onClick={it.onClick}
                          className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                          <Icon className="h-4 w-4 shrink-0 text-slate-400" /> {it.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
            <div className="grid grid-cols-1 gap-0.5">
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <button
                    key={it.key}
                    data-testid={`${testid}-item-${it.key}`}
                    onClick={it.onClick}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-slate-400" /> {it.label}
                    {it.count != null && <span className="ml-auto rounded-full bg-slate-800/80 px-1.5 py-0.5 text-[10px] text-slate-400">{it.count}</span>}
                  </button>
                );
              })}
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Sekcja menu na telefonie: nagłówek rozwija listę kategorii (albo kolumny gier World/Race)
function MobileRow({ it }) {
  return (
    <button onClick={it.onClick} className="flex min-w-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm text-slate-300 active:bg-white/10">
      {it.icon && <it.icon className="h-4 w-4 shrink-0 text-cyan-300/80" />} <span className="truncate">{it.label}</span>
    </button>
  );
}

function MobileSection({ title, icon: Icon, accent = "text-slate-100", open, onToggle, all, items = [], groups }) {
  return (
    <div className="border-b border-cyan-500/10">
      <button onClick={onToggle} aria-expanded={open} className={`flex w-full items-center justify-between py-3.5 text-left text-[15px] font-bold ${accent}`}>
        <span className="flex items-center gap-2.5"><Icon className="h-5 w-5" /> {title}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-3">
          {all && <button onClick={all.onClick} className="mb-1 w-full rounded-xl bg-cyan-500/10 px-3 py-2.5 text-left text-sm font-bold text-cyan-300">{all.label} →</button>}
          {groups ? groups.map((g) => (
            <div key={g.key} className="mt-1">
              <button onClick={g.onClick} className="w-full px-3 py-2 text-left text-xs font-extrabold uppercase tracking-wider text-amber-300">{g.title} →</button>
              <div className="grid grid-cols-2">{g.items.map((it) => <MobileRow key={it.key} it={it} />)}</div>
            </div>
          )) : <div className="grid grid-cols-2">{items.map((it) => <MobileRow key={it.key} it={it} />)}</div>}
        </div>
      )}
    </div>
  );
}

export function Navbar() {
  const { count, setOpen } = useCart();
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [mobileSection, setMobileSection] = useState(null); // rozwinięta sekcja menu na telefonie
  const toggleSection = (key) => setMobileSection((cur) => (cur === key ? null : key));
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("dl-theme") || "dark"; } catch { return "dark"; }
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [bookCats, setBookCats] = useState([]);
  const [openMenu, setOpenMenu] = useState(null); // null | "ebooki" | "audiobooki" | "aplikacje" | "gry"
  const closeTimer = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => setBookCats(r.data.filter((c) => !BOOK_CATS_EXCLUDE.has(c.slug)))).catch(() => {});
  }, []);

  const openNow = (key) => { clearTimeout(closeTimer.current); setOpenMenu(key); };
  const closeSoon = () => { closeTimer.current = setTimeout(() => setOpenMenu(null), 180); };

  const goFilter = (key) => {
    setMobile(false);
    setOpenMenu(null);
    const apply = () => {
      window.dispatchEvent(new CustomEvent("set-category-filter", { detail: key }));
      document.getElementById("katalog")?.scrollIntoView({ behavior: "smooth" });
    };
    // z innej podstrony: kategoria w adresie — strona główna ustawi ją sama po wczytaniu katalogu
    if (location.pathname !== "/") navigate(`/?kat=${encodeURIComponent(key)}#katalog`);
    else apply();
  };

  // kategorie aplikacji zawsze prowadzą do strony /aplikacje z wybraną kategorią (jedno, przewidywalne miejsce)
  const goAppCategory = (id) => {
    setMobile(false);
    setOpenMenu(null);
    navigate(`/aplikacje?cat=${id}`);
    window.scrollTo({ top: 0 });
  };

  // seria "gry" = DigiLib World (#gry, ?gra=), "race" = DigiLib Race (#race, ?race=)
  const goGameSubject = (slug, series = "gry") => {
    setMobile(false);
    setOpenMenu(null);
    const apply = () => {
      window.dispatchEvent(new CustomEvent(series === "race" ? "set-race-subject" : "set-games-subject", { detail: slug }));
      document.getElementById(series)?.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") navigate(`/?${series === "race" ? "race" : "gra"}=${encodeURIComponent(slug)}#${series}`);
    else apply();
  };

  const goSection = (id) => {
    setMobile(false);
    const apply = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    if (location.pathname !== "/") navigate(`/#${id}`); // przewinięcie po wczytaniu strony głównej (Home, kotwica)
    else apply();
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem("dl-theme", theme); } catch {}
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [location.pathname]);
  // otwarte menu na telefonie: bez przewijania strony pod spodem i bez dymka czatu nad menu (index.css: .dl-menu-open)
  useEffect(() => {
    document.documentElement.classList.toggle("dl-menu-open", mobile);
    return () => document.documentElement.classList.remove("dl-menu-open");
  }, [mobile]);

  return (
    <header className={`fixed inset-x-0 top-0 ${mobile ? "z-[60] bg-slate-950" : "z-40"} transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-slate-950/85 shadow-lg shadow-cyan-500/5 backdrop-blur-2xl border-b border-cyan-500/15" : "bg-transparent"}`}>
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3 sm:px-6 lg:py-4">
        <Link to="/" data-testid="nav-logo" className="flex min-w-0 items-center gap-2 sm:gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30 sm:h-10 sm:w-10">
            <Library className="h-5 w-5 text-white" />
          </div>
          <span className="truncate font-display text-lg font-extrabold tracking-tight sm:text-xl">
            <span className="gradient-text">DigiLib</span><span className="text-slate-400">.store</span>
          </span>
        </Link>

        <div className="hidden items-center gap-4 lg:flex xl:gap-6">
          <NavMenu
            label={t("nav.ebooks")} labelClassName="font-medium text-slate-300 hover:text-cyan-300" testid="nav-ebooks"
            isOpen={openMenu === "ebooki"} onOpen={() => openNow("ebooki")} onCloseSoon={closeSoon}
            onLabelClick={() => goFilter("format:ebook")}
            items={bookCats.map((c) => ({ key: c.slug, label: c.name, icon: BOOK_ICONS[c.icon] || BookOpen, count: c.count, onClick: () => goFilter(`fmtcat:ebook:${c.slug}`) }))}
          />

          <NavMenu
            label={t("nav.audiobooks")} labelClassName="font-medium text-slate-300 hover:text-cyan-300" testid="nav-audiobooks"
            isOpen={openMenu === "audiobooki"} onOpen={() => openNow("audiobooki")} onCloseSoon={closeSoon}
            onLabelClick={() => goFilter("format:audiobook")}
            items={bookCats.map((c) => ({ key: c.slug, label: c.name, icon: BOOK_ICONS[c.icon] || Headphones, onClick: () => goFilter(`fmtcat:audiobook:${c.slug}`) }))}
          />

          <NavMenu
            label={t("nav.apps")} labelClassName="font-medium text-slate-300 hover:text-fuchsia-300" testid="nav-apps"
            isOpen={openMenu === "aplikacje"} onOpen={() => openNow("aplikacje")} onCloseSoon={closeSoon}
            onLabelClick={() => { setOpenMenu(null); navigate("/aplikacje"); }}
            items={APP_CATEGORIES.filter((c) => c.id !== "pakiety").map((c) => ({ key: c.id, label: c.label, icon: APP_CAT_ICONS[c.id] || Smartphone, onClick: () => goAppCategory(c.id) }))}
          />

          <NavMenu
            label={t("nav.games")} labelClassName="font-bold text-amber-300 hover:text-amber-200" testid="nav-games"
            isOpen={openMenu === "gry"} onOpen={() => openNow("gry")} onCloseSoon={closeSoon}
            onLabelClick={() => goSection("gry")}
            columns={[
              { key: "world", title: "DigiLib World", onClick: () => goGameSubject("all"), items: GAME_SUBJECTS.map((s) => ({ key: s.slug, label: s.label, icon: s.icon, onClick: () => goGameSubject(s.slug) })) },
              { key: "race", title: "DigiLib Race", onClick: () => goGameSubject("all", "race"), items: GAME_SUBJECTS.map((s) => ({ key: s.slug, label: s.label, icon: s.icon, onClick: () => goGameSubject(s.slug, "race") })) },
            ]}
          />

          <Link to="/blog" className="hidden text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300 xl:block">{t("nav.blog")}</Link>
          <Link to="/kontakt" data-testid="nav-contact" className="text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300">{t("nav.contact")}</Link>
          <Link to="/konto" data-testid="nav-account" className="flex items-center gap-1.5 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"><User className="h-4 w-4" /> {t("nav.account")}</Link>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <SiteSearch />
          <div className="hidden lg:block"><LanguageSwitcher /></div>
          <button
            data-testid="theme-toggle"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="hidden rounded-full glass-panel p-2.5 lg:block text-slate-200 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
            aria-label={theme === "dark" ? t("theme.toLight.aria") : t("theme.toDark.aria")}
            title={theme === "dark" ? t("theme.toLight.title") : t("theme.toDark.title")}
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            data-testid="nav-book-request"
            onClick={() => window.dispatchEvent(new Event("open-book-request"))}
            className="hidden items-center gap-1.5 rounded-full border border-fuchsia-400/40 bg-gradient-to-r from-fuchsia-500/20 to-cyan-500/20 px-4 py-2 text-xs font-bold text-fuchsia-300 transition-[transform,border-color] duration-200 hover:scale-105 hover:border-fuchsia-300/70 xl:flex"
          >
            <MailQuestion className="h-3.5 w-3.5" /> {t("nav.bookRequest")}
          </button>
          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative rounded-full glass-panel p-2.5 text-slate-200 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
            aria-label={t("nav.cart.aria")}
          >
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 text-[10px] font-bold text-slate-950">
                {count}
              </span>
            )}
          </button>
          <button className="rounded-full glass-panel p-2.5 text-slate-200 lg:hidden" onClick={() => setMobile(!mobile)} aria-label={t("nav.menu.aria")} aria-expanded={mobile} data-testid="nav-mobile-menu">
            {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      {mobile && (
        <div className="h-[calc(100dvh-64px)] overflow-y-auto overscroll-contain border-t border-cyan-500/10 bg-slate-950 px-4 pb-6 pt-2 sm:px-6 lg:hidden" data-testid="nav-mobile-panel">
          <MobileSection title={t("nav.ebooks")} icon={BookOpen} open={mobileSection === "ebooki"} onToggle={() => toggleSection("ebooki")}
            all={{ label: t("nav.mobile.all"), onClick: () => goFilter("format:ebook") }}
            items={bookCats.map((c) => ({ key: c.slug, label: c.name, icon: BOOK_ICONS[c.icon] || BookOpen, onClick: () => goFilter(`fmtcat:ebook:${c.slug}`) }))} />
          <MobileSection title={t("nav.audiobooks")} icon={Headphones} open={mobileSection === "audiobooki"} onToggle={() => toggleSection("audiobooki")}
            all={{ label: t("nav.mobile.all"), onClick: () => goFilter("format:audiobook") }}
            items={bookCats.map((c) => ({ key: c.slug, label: c.name, icon: BOOK_ICONS[c.icon] || Headphones, onClick: () => goFilter(`fmtcat:audiobook:${c.slug}`) }))} />
          <MobileSection title={t("nav.apps")} icon={Smartphone} accent="text-fuchsia-300" open={mobileSection === "aplikacje"} onToggle={() => toggleSection("aplikacje")}
            all={{ label: t("nav.mobile.all"), onClick: () => { setMobile(false); navigate("/aplikacje"); } }}
            items={APP_CATEGORIES.filter((c) => c.id !== "pakiety").map((c) => ({ key: c.id, label: c.label, icon: APP_CAT_ICONS[c.id] || Smartphone, onClick: () => goAppCategory(c.id) }))} />
          <MobileSection title={t("nav.games.mobile")} icon={Gamepad2} accent="text-amber-300" open={mobileSection === "gry"} onToggle={() => toggleSection("gry")}
            groups={[
              { key: "world", title: "DigiLib World", onClick: () => goGameSubject("all"), items: GAME_SUBJECTS.map((x) => ({ key: x.slug, label: x.label, icon: x.icon, onClick: () => goGameSubject(x.slug) })) },
              { key: "race", title: "DigiLib Race", onClick: () => goGameSubject("all", "race"), items: GAME_SUBJECTS.map((x) => ({ key: x.slug, label: x.label, icon: x.icon, onClick: () => goGameSubject(x.slug, "race") })) },
            ]} />
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Link to="/konto" data-testid="nav-account-mobile" className="flex items-center gap-2 rounded-xl glass-panel px-3 py-3 text-sm font-semibold text-slate-200"><User className="h-4 w-4 text-cyan-300" /> {t("nav.account.mobile")}</Link>
            <button onClick={() => { setMobile(false); window.dispatchEvent(new Event("open-book-request")); }} data-testid="nav-book-request-mobile" className="flex items-center gap-2 rounded-xl glass-panel px-3 py-3 text-left text-sm font-semibold text-fuchsia-300"><MailQuestion className="h-4 w-4 shrink-0" /> {t("nav.bookRequest")}</button>
            <Link to="/blog" className="rounded-xl glass-panel px-3 py-3 text-sm font-medium text-slate-200">{t("nav.blog")}</Link>
            <Link to="/kontakt" data-testid="nav-contact-mobile" className="rounded-xl glass-panel px-3 py-3 text-sm font-medium text-slate-200">{t("nav.contact")}</Link>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-cyan-500/10 pt-4">
            <div className="flex items-center gap-2 text-sm text-slate-400">{t("nav.mobile.language")} <LanguageSwitcher /></div>
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle-mobile"
              className="flex items-center gap-2 rounded-full glass-panel px-3.5 py-2.5 text-sm text-slate-200">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {t("nav.mobile.theme")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
